import Vue from 'vue';
import { handleMdfUpdatesToItem } from '@mimir/common/mdf/update';
import type { LoadedItem } from '@mimir/common/dto/searchResultHit';
import type { VueDeleteValue, VueSetValue } from '@mimir/common/util/general';
import type { TimelineModuleState, UpdateCurrentItemPayload } from './timeline-module-state';

export function updateCurrentItemMutation(state: TimelineModuleState, payload: UpdateCurrentItemPayload) {
  if (!state.currentItem || payload.id !== state.currentItem.id) return;

  /*
    `payload.retrievedFields` contains a list of keys fetched from the server.
    Since `undefined` values are not transmitted over the API, we combine the
    fetched keys with those available in the `payload.update` object.

    This ensures that the client state is updated with the most recent data
    while correctly handling stale state updates.
  */
  const uniqueKeys = new Set([...Object.keys(payload.update), ...(payload.retrievedFields ?? [])]);
  const keysToIterateOver = Array.from(uniqueKeys) as (keyof LoadedItem)[];

  const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
  const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
  for (const prop of keysToIterateOver) {
    if (prop === 'metadata' && payload.update.metadata !== undefined) {
      let metadataVersion = payload.update.metadataVersion;
      if (metadataVersion == undefined) {
        console.warn('updateCurrentPreviewItemMutation: metadata update without metadataVersion');
        metadataVersion = 0;
      }
      handleMdfUpdatesToItem(state.currentItem, payload.update.metadata, metadataVersion, vueSet, vueDeleter);
    } else if (prop === 'media') {
      // The live media info we get from search cannot be expected to
      // be up to date. So we avoid overwriting item.media with data
      // that looks like it is older than what we already have.
      const currentCount = state.currentItem.media?.segmentCount;
      const updatedCount = payload.update.media?.segmentCount;
      if (currentCount === undefined || (updatedCount ?? -1) >= currentCount) {
        Vue.set(state.currentItem, prop, payload.update[prop]);
      }
    } else if (payload.update[prop] === undefined) {
      Vue.delete(state.currentItem, prop);
    } else {
      Vue.set(state.currentItem, prop, payload.update[prop]);
    }
  }
}

